import React, { useEffect, useReducer, useMemo } from 'react';
import api from '../../../utils/api';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { showToast } from '../../../Components/ToastNotifications';

// --- Reducer untuk state management ---
const initialState = {
  loading: false,
  fetching: true,
  error: null,
  mentess: [],
  successMessage: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, fetching: true, error: null };
    case 'FETCH_SUCCESS':
      return { ...state, fetching: false, mentess: action.payload };
    case 'FETCH_ERROR':
      return { ...state, fetching: false, error: action.payload };
    case 'UPDATE_EVALUATION':
      return {
        ...state,
        mentess: state.mentess.map((item, i) =>
          i === action.key ? { ...item, evaluation: action.value } : item,
        ),
      };
    case 'UPDATE_NOTE':
      return {
        ...state,
        mentess: state.mentess.map((item, i) =>
          i === action.key ? { ...item, note: action.value } : item,
        ),
      };
    case 'SAVE_START':
      return { ...state, loading: true, error: null, successMessage: null };
    case 'SAVE_SUCCESS':
      return { ...state, loading: false, successMessage: 'Berhasil disimpan!' };
    case 'SAVE_ERROR':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
}

// --- Skala Penilaian 0-100 (sesuai docs/evaluation/bobot_evaluation.md, kolom "Makna (KPI)") ---
const SCORE_BANDS = [
  { min: 90, max: 100, label: 'Sangat Baik', meaning: 'Capaian melebihi target' },
  { min: 80, max: 89, label: 'Baik', meaning: 'Capaian sesuai target' },
  { min: 70, max: 79, label: 'Cukup', meaning: 'Capaian sebagian besar tercapai' },
  { min: 60, max: 69, label: 'Kurang', meaning: 'Capaian di bawah target' },
  { min: 0, max: 59, label: 'Sangat Kurang', meaning: 'Capaian jauh dari target' },
];

// --- Komponen terpisah untuk satu KPI field ---
const KpiField = ({ value, index, onChangeNilai, onChangeCatatan, maxVal }) => {
  const isAutomatic = value.classificationTypeCode === 'CB';

  return (
    <div
      key={index}
      className='card border-0 shadow-sm rounded-4 mb-4 overflow-hidden'
    >
      <div className='card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center'>
        <div className='d-flex align-items-center gap-2'>
          <span
            className='badge bg-primary rounded-circle d-flex align-items-center justify-content-center'
            style={{ width: '28px', height: '28px', fontSize: '0.85rem' }}
          >
            {index + 1}
          </span>
          <h6 className='fw-bold text-dark mb-0'>Key Performance Indicator</h6>
          {isAutomatic && (
            <span className='badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 rounded-pill'>
              Dihitung otomatis
            </span>
          )}
        </div>
        {!isAutomatic && (
          <div
            className='d-flex align-items-center gap-2'
            style={{ maxWidth: '160px' }}
          >
            <span className='small text-muted fw-medium text-nowrap'>
              Nilai (0-{maxVal}):
            </span>
            <input
              type='number'
              value={value.evaluation || ''}
              onChange={(e) => onChangeNilai(e.target.value, index)}
              onWheel={(e) => e.target.blur()}
              placeholder='0'
              className='form-control form-control-sm text-center fw-bold border-light-subtle rounded-pill'
              min={0}
              max={maxVal}
              required
              style={{ width: '65px' }}
            />
          </div>
        )}
      </div>
      <div className='card-body p-4'>
        <div className='mb-3 p-3 rounded-3 bg-light border border-light-subtle'>
          <p
            className='mb-0 text-secondary small fw-medium style-description'
            style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5' }}
          >
            {value.classification}
          </p>
        </div>

        {isAutomatic ? (
          <div className='p-3 rounded-3 bg-info bg-opacity-10 border border-info border-opacity-25 d-flex align-items-center justify-content-between'>
            <span className='small fw-medium text-info'>
              Nilai Capacity Building dihitung otomatis dari Pre-test (20%),
              Post-test (40%), dan Tugas (40%).
            </span>
            <span className='fw-bold text-info fs-5'>{value.evaluation ?? '-'}</span>
          </div>
        ) : (
          <>
            <div className='mb-3 p-3 rounded-3 bg-light border border-light-subtle'>
              <p className='small fw-bold text-dark mb-2'>Skala Penilaian</p>
              <div className='d-grid gap-1'>
                {SCORE_BANDS.map((band) => (
                  <div
                    key={band.label}
                    className='d-flex align-items-center gap-2'
                    style={{ fontSize: '0.75rem' }}
                  >
                    <span
                      className='badge bg-white text-dark border border-light-subtle fw-bold text-nowrap'
                      style={{ minWidth: '58px' }}
                    >
                      {band.min}-{band.max}
                    </span>
                    <span
                      className='fw-medium text-secondary text-nowrap'
                      style={{ minWidth: '96px' }}
                    >
                      {band.label}
                    </span>
                    <span className='text-muted'>{band.meaning}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className='mb-0'>
              <label className='form-label small fw-bold text-dark d-flex align-items-center gap-1 mb-2'>
                <i className='bi bi-chat-right-text text-primary'></i>
                Catatan Evaluasi / Bukti Kinerja
              </label>
              <textarea
                rows={3}
                value={value.note || ''}
                onChange={(e) => onChangeCatatan(e.target.value, index)}
                placeholder='Tuliskan catatan detail atau bukti pencapaian KPI ini...'
                className='form-control border-light-subtle bg-light bg-opacity-25 shadow-none'
                style={{ fontSize: '0.9rem' }}
                required
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const EvaluationHSAdd = () => {
  const location = useLocation();
  const { data } = location.state || {};
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState);

  const isBumdes = data?.programCode === 'BUMDES';
  const maxVal = 100;
  const isAdmin = window.location.pathname.startsWith('/admin');

  // --- Fetch data ---
  useEffect(() => {
    const fetchEvaluations = async () => {
      try {
        dispatch({ type: 'FETCH_START' });
        const res = await api.getFinalHardSkillEvaluations({ id });
        dispatch({ type: 'FETCH_SUCCESS', payload: res });
      } catch (err) {
        dispatch({ type: 'FETCH_ERROR', payload: 'Gagal mengambil data KPI' });
      }
    };
    fetchEvaluations();
  }, [id]);

  // --- Validation: disable save button jika tidak lengkap ---
  // KPI bertipe CB (Capacity Building) diisi otomatis oleh sistem, jadi dikecualikan dari validasi ini.
  const isDisabled = useMemo(() => {
    if (!state.mentess?.length) return true;
    return state.mentess
      .filter((item) => item.classificationTypeCode !== 'CB')
      .some(
        (item) =>
          item.evaluation === null ||
          item.evaluation === undefined ||
          item.evaluation === '' ||
          item.evaluation < 0 ||
          item.evaluation > maxVal ||
          !item.note?.trim(),
      );
  }, [state.mentess, maxVal]);

  // --- Handlers ---
  const handleChangeNilai = (value, index) => {
    const num = parseInt(value, 10);
    if (num > maxVal) {
      showToast(`Nilai tidak boleh lebih dari ${maxVal}!`, 'danger');
      return;
    }
    dispatch({ type: 'UPDATE_EVALUATION', key: index, value: num });
  };

  const handleChangeCatatan = (value, index) => {
    dispatch({ type: 'UPDATE_NOTE', key: index, value });
  };

  const handleSubmit = async () => {
    try {
      dispatch({ type: 'SAVE_START' });
      // KPI bertipe CB (Capacity Building) diisi otomatis oleh sistem — jangan ikut ditimpa dari form ini.
      const payload = {
        finalEvaluationsId: parseInt(id),
        evaluates: state.mentess.filter((item) => item.classificationTypeCode !== 'CB'),
      };
      await api.updateFinalHardSkillEvaluations({ id, payload });
      showToast('Berhasil disimpan!', 'success');
      dispatch({ type: 'SAVE_SUCCESS' });
      navigate(
        isAdmin
          ? `/admin/students/${id}/evaluations`
          : `/mentor/mentees/${id}/evaluations`,
      );
    } catch (err) {
      showToast('Gagal menyimpan penilaian!', 'danger');
      dispatch({ type: 'SAVE_ERROR', payload: 'Gagal menyimpan penilaian!' });
    }
  };

  // --- UI utama ---
  return (
    <div className='container-fluid p-0 pb-5'>
      {/* Premium Header */}
      <div className='card border-0 rounded-4 mb-4 overflow-hidden shadow-sm'>
        <div className='card-body p-4'>
          <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3'>
            <div className='d-flex align-items-center gap-3'>
              <button
                onClick={() =>
                  navigate(
                    isAdmin
                      ? `/admin/students/${id}/evaluations`
                      : `/mentor/mentees/${id}/evaluations`,
                  )
                }
                className='btn btn-light rounded-circle d-flex align-items-center justify-content-center border transition-all hover-scale'
                style={{ width: '40px', height: '40px' }}
              >
                <i className='bi bi-arrow-left'></i>
              </button>
              <div>
                <span className='badge bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-pill fw-bold small mb-1'>
                  {isBumdes ? 'Penilaian KPI' : 'Penilaian Hard Skill'}
                </span>
                <h1 className='h4 fw-bold mb-0 text-dark'>
                  {data?.mentessFirstName} {data?.mentessLastName}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='row g-4'>
        {/* Left Column: KPI / Hardskill Fields */}
        <div className='col-lg-8 order-lg-1 order-2'>
          {state.fetching ? (
            <div className='d-flex justify-content-center align-items-center py-5'>
              <div
                className='spinner-border text-primary'
                role='status'
              >
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            <div className='d-grid gap-3'>
              {state.error && (
                <div className='alert alert-danger rounded-3 py-3 px-4 fw-medium d-flex align-items-center gap-2'>
                  <i className='bi bi-exclamation-triangle-fill'></i>
                  {state.error}
                </div>
              )}
              {state.successMessage && (
                <div className='alert alert-success rounded-3 py-3 px-4 fw-medium d-flex align-items-center gap-2'>
                  <i className='bi bi-check-circle-fill'></i>
                  {state.successMessage}
                </div>
              )}

              {state.mentess?.map((value, index) => (
                <KpiField
                  key={index}
                  value={value}
                  index={index}
                  onChangeNilai={handleChangeNilai}
                  onChangeCatatan={handleChangeCatatan}
                  maxVal={maxVal}
                />
              ))}

              <div className='d-grid mt-2'>
                <button
                  disabled={isDisabled || state.loading}
                  className='btn btn-primary btn-lg fw-bold rounded-pill shadow d-flex align-items-center justify-content-center gap-2 hover-scale'
                  type='button'
                  onClick={handleSubmit}
                >
                  {state.loading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Menyimpan Penilaian...
                    </>
                  ) : (
                    <>
                      <i className='bi bi-save2'></i> Simpan Semua Penilaian
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Profil Mahasiswa */}
        <div className='col-lg-4 order-lg-2 order-1'>
          <div
            className='d-grid gap-4 sticky-top'
            style={{ top: '1.5rem', zIndex: 10 }}
          >
            {/* User Info Card */}
            <div className='card border-0 rounded-4 shadow-sm'>
              <div className='card-body p-4'>
                <h6 className='fw-bold text-dark mb-4 pb-2 border-bottom'>
                  Profil Mahasiswa
                </h6>
                <div className='row g-3'>
                  {data?.mentors && (
                    <div className='col-12 col-md-6 col-lg-12'>
                      <div className='p-3 rounded-3 bg-light border border-light-subtle h-100'>
                        <label className='small text-muted mb-1 d-block fw-medium'>
                          <i className='bi bi-person-badge me-2 text-primary'></i>
                          Mentor Pembimbing
                        </label>
                        <span className='fw-bold text-dark small'>
                          {data?.mentors?.firstName} {data?.mentors?.lastName}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className='col-12 col-md-6 col-lg-12'>
                    <div className='p-3 rounded-3 bg-light border border-light-subtle h-100'>
                      <label className='small text-muted mb-1 d-block fw-medium'>
                        <i className='bi bi-book me-2 text-primary'></i>
                        Jurusan
                      </label>
                      <span className='fw-semibold text-dark small'>
                        {data?.universities?.universitiesMajor || '-'}
                      </span>
                    </div>
                  </div>

                  <div className='col-12 col-md-6 col-lg-12'>
                    <div className='p-3 rounded-3 bg-light border border-light-subtle h-100'>
                      <label className='small text-muted mb-1 d-block fw-medium'>
                        <i className='bi bi-mortarboard me-2 text-primary'></i>
                        Universitas
                      </label>
                      <span className='fw-semibold text-dark small'>
                        {data?.universities?.universitiesName || '-'}
                      </span>
                    </div>
                  </div>

                  <div className='col-12 col-md-6 col-lg-12'>
                    <div className='p-3 rounded-3 bg-light border border-light-subtle h-100'>
                      <label className='small text-muted mb-1 d-block fw-medium'>
                        <i className='bi bi-briefcase me-2 text-primary'></i>
                        Divisi Penempatan
                      </label>
                      <div className='d-block mt-1'>
                        <span className='fw-semibold text-dark small'>
                          {data?.institutions?.divisionName || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .hover-scale {
          transition: transform 0.2s ease, background-color 0.2s ease;
        }
        .hover-scale:hover {
          transform: scale(1.02);
        }
        .style-description {
          font-family: inherit;
        }
      `}</style>
    </div>
  );
};

export default EvaluationHSAdd;
